const allowedTags = new Set([
  'a',
  'article',
  'b',
  'blockquote',
  'br',
  'code',
  'div',
  'em',
  'figcaption',
  'figure',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'img',
  'li',
  'ol',
  'p',
  'pre',
  'rp',
  'rt',
  'ruby',
  's',
  'section',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
])

const removedTags = new Set([
  'embed',
  'form',
  'iframe',
  'link',
  'meta',
  'object',
  'script',
  'style',
  'template',
])

const globalAllowedAttributes = new Set([
  'aria-label',
  'aria-hidden',
  'class',
  'colspan',
  'dir',
  'id',
  'lang',
  'rowspan',
  'style',
  'title',
])

const allowedAttributesByTag: Readonly<Record<string, readonly string[]>> = {
  a: ['href', 'rel', 'target'],
  img: ['alt', 'height', 'loading', 'src', 'width'],
}

function isSafeUrl(value: string) {
  const trimmedValue = value.trim()

  if (
    trimmedValue.startsWith('/') ||
    trimmedValue.startsWith('#') ||
    trimmedValue.startsWith('./') ||
    trimmedValue.startsWith('../')
  ) {
    return true
  }

  try {
    const url = new URL(trimmedValue, window.location.origin)

    return (
      url.protocol === 'http:' ||
      url.protocol === 'https:' ||
      url.protocol === 'mailto:' ||
      url.protocol === 'tel:' ||
      (url.protocol === 'data:' && trimmedValue.startsWith('data:image/'))
    )
  } catch {
    return false
  }
}

function isSafeStyle(value: string) {
  const normalizedValue = value.toLowerCase()

  return !(
    normalizedValue.includes('expression') ||
    normalizedValue.includes('@import') ||
    normalizedValue.includes('url(') ||
    normalizedValue.includes('javascript:') ||
    normalizedValue.includes('vbscript:')
  )
}

function isAllowedAttribute(tagName: string, attributeName: string) {
  if (attributeName.startsWith('on') || attributeName === 'srcdoc') {
    return false
  }

  if (attributeName.startsWith('data-')) {
    return true
  }

  return (
    globalAllowedAttributes.has(attributeName) ||
    (allowedAttributesByTag[tagName] ?? []).includes(attributeName)
  )
}

function sanitizeElementAttributes(element: Element, tagName: string) {
  Array.from(element.attributes).forEach((attribute) => {
    const attributeName = attribute.name.toLowerCase()
    const attributeValue = attribute.value

    if (!isAllowedAttribute(tagName, attributeName)) {
      element.removeAttribute(attribute.name)
      return
    }

    if ((attributeName === 'href' || attributeName === 'src') && !isSafeUrl(attributeValue)) {
      element.removeAttribute(attribute.name)
      return
    }

    if (attributeName === 'style' && !isSafeStyle(attributeValue)) {
      element.removeAttribute(attribute.name)
      return
    }
  })

  if (tagName === 'a' && element.getAttribute('target') === '_blank') {
    element.setAttribute('rel', 'noopener noreferrer')
  }

  if (tagName === 'img' && !element.getAttribute('loading')) {
    element.setAttribute('loading', 'lazy')
  }
}

function sanitizeNode(node: Node) {
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return
  }

  const element = node as Element
  const tagName = element.tagName.toLowerCase()

  if (removedTags.has(tagName)) {
    element.remove()
    return
  }

  if (!allowedTags.has(tagName)) {
    const childNodes = Array.from(element.childNodes)
    childNodes.forEach(sanitizeNode)
    element.replaceWith(...childNodes)
    return
  }

  sanitizeElementAttributes(element, tagName)
  Array.from(element.childNodes).forEach(sanitizeNode)
}

export function sanitizeHtml(html: string) {
  if (!html) {
    return ''
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return html.replace(/<[^>]*>/g, '')
  }

  const template = document.createElement('template')
  template.innerHTML = html
  Array.from(template.content.childNodes).forEach(sanitizeNode)

  return template.innerHTML
}
